import React from "react";
import { api } from "@/lib/api";

const PROVINCES = ["Brabant wallon", "Bruxelles-Capitale", "Hainaut", "Liège", "Luxembourg", "Namur"];

/**
 * Cascading province → code postal → school selector.
 * value: { province, school_code_postal, school_id, school_name, school_commune }
 * onChange: (next) => void
 */
export default function SchoolSelect({ value = {}, onChange, required = false, testIdPrefix = "" }) {
  const [postalCodes, setPostalCodes] = React.useState([]);
  const [schools, setSchools] = React.useState([]);
  const [loadingPC, setLoadingPC] = React.useState(false);
  const [loadingSch, setLoadingSch] = React.useState(false);

  React.useEffect(() => {
    if (!value.province) { setPostalCodes([]); setSchools([]); return; }
    setLoadingPC(true);
    api.get("/postal-codes", { params: { province: value.province } })
      .then((r) => setPostalCodes(r.data))
      .catch(() => setPostalCodes([]))
      .finally(() => setLoadingPC(false));
  }, [value.province]);

  React.useEffect(() => {
    if (!value.province || !value.school_code_postal) { setSchools([]); return; }
    setLoadingSch(true);
    api.get("/schools", { params: { province: value.province, code_postal: value.school_code_postal } })
      .then((r) => setSchools(r.data))
      .catch(() => setSchools([]))
      .finally(() => setLoadingSch(false));
  }, [value.province, value.school_code_postal]);

  const setProvince = (p) => {
    onChange({ ...value, province: p, school_code_postal: "", school_id: "", school_name: "", school_commune: "" });
  };
  const setPostalCode = (cp) => {
    onChange({ ...value, school_code_postal: cp, school_id: "", school_name: "", school_commune: "" });
  };
  const setSchool = (school_id) => {
    const s = schools.find((x) => x.school_id === school_id);
    onChange({
      ...value,
      school_id,
      school_name: s?.nom_ecole || "",
      school_commune: s?.commune || "",
      school_code_postal: s?.code_postal || value.school_code_postal,
    });
  };

  return (
    <div className="grid sm:grid-cols-3 gap-3 sm:col-span-2">
      <select
        className="neo-input"
        value={value.province || ""}
        onChange={(e) => setProvince(e.target.value)}
        required={required}
        data-testid={`${testIdPrefix}province-select`}
      >
        <option value="">Choisis ta province</option>
        {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select
        className="neo-input"
        value={value.school_code_postal || ""}
        onChange={(e) => setPostalCode(e.target.value)}
        disabled={!value.province || loadingPC}
        required={required}
        data-testid={`${testIdPrefix}cp-select`}
      >
        <option value="">{!value.province ? "Choisis la province d'abord" : loadingPC ? "Chargement…" : `${postalCodes.length} codes postaux`}</option>
        {postalCodes.map((p) => (
          <option key={p.code_postal} value={p.code_postal}>{p.code_postal} {p.commune} ({p.count})</option>
        ))}
      </select>
      <select
        className="neo-input"
        value={value.school_id || ""}
        onChange={(e) => setSchool(e.target.value)}
        disabled={!value.school_code_postal || loadingSch}
        required={required}
        data-testid={`${testIdPrefix}school-select`}
      >
        <option value="">{!value.school_code_postal ? "Choisis le code postal" : loadingSch ? "Chargement…" : `${schools.length} école·s`}</option>
        {schools.map((s) => (
          <option key={s.school_id} value={s.school_id}>{s.nom_ecole}</option>
        ))}
      </select>
    </div>
  );
}
